import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { sv } from "date-fns/locale";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { isNumber, validateDateFormat } from "../../util/validators";
import { UPDATE_TRANSACTION } from "../../redux/reducers/transactionReducer";
import { TransactionDTO } from "../../generated/api";
import { useState } from "react";
import { addStaticTimeToDate } from "../../util/formats";

export function UpdateTransactionModal() {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const transaction = useSelector(
    (state: any) => state.transaction.transaction
  );
  const extendedCard = useSelector((state: any) => state.card.extendedCard);
  const cardTypes = useSelector((state: any) => state.cardTypes.cardTypes);
  const currentUser = useSelector((state: any) => state.session.currentUser);
  const carePrincipal = useSelector(
    (state: any) => state.session.selectedCarePrincipal
  );
  const cardSystemId = extendedCard.cardSystemId;

  const [transactionDate, setTransactionDate] = useState(
    new Date(transaction.transactionDate)
  );
  const [transactionDateError, setTransactionDateError] = useState(false);

  let cardType = cardTypes.find((o: any) => o.id === extendedCard.cardType.id);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const handleTransactionDateChange = (date: any) => {
    if (date != null && validateDateFormat(date)) {
      setTransactionDate(date);
      setTransactionDateError(false);
    } else {
      setTransactionDate(date);
      setTransactionDateError(true);
    }
  };

  const onSubmit = (values: any) => {
    const updateTransaction = {
      actorId: currentUser.employeeHsaId,
      actorName: currentUser.name,
      personId: transaction.personId,
      amount: Number(values.amount),
      transactionDate: addStaticTimeToDate(transactionDate),
      carePrincipal: { id: carePrincipal.id, name: carePrincipal.name },
      careProvider: values.careProvider,
      careProviderId: transaction.careProviderId,
      careUnit: values.careUnit,
      careUnitId: transaction.careUnitId,
      transactionId: transaction.transactionId,
      registrationDate: new Date(transaction.registrationDateTime),
      status: "",
      statusReason: values.reason,
      registeringCareProviderId: transaction.registeringCareProviderId,
    } as TransactionDTO;
    dispatch({ type: UPDATE_TRANSACTION, updateTransaction, cardType, cardSystemId });
  };

  return (
    <Dialog
      fullWidth={true}
      maxWidth={"lg"}
      open={true}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle sx={{ fontWeight: "bold" }} id="alert-dialog-title">
          {t("searchCard.manageTxns.updateTransaction")}
        </DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={4}>
            <Typography sx={{ fontWeight: "bold" }}>
              {t("searchCard.form.personIdLabel")}
            </Typography>
            <Typography sx={{ fontWeight: "bold" }}>
              {t("searchCard.form.cardTypeLabel")}
            </Typography>
            <Typography sx={{ fontWeight: "bold" }}>
              {t("carePrincipal")}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={4} sx={{ paddingBottom: 2 }}>
            <Typography>{extendedCard.persons[0].id}</Typography>
            <Typography>{cardType.name}</Typography>
            <Typography>{transaction.carePrincipal.id}</Typography>
          </Stack>
          <Stack spacing={3}>
            <Controller
              name="careProvider"
              control={control}
              render={({ field: { value } }) => (
                <TextField
                  size="small"
                  error={errors.careProvider ? true : false}
                  defaultValue={transaction.careProvider}
                  value={value}
                  id="careProvider"
                  label={t("careProvider") + " *"}
                  {...register("careProvider", { required: true })}
                />
              )}
            />
            {errors.careProvider && errors.careProvider.type === "required" && (
              <Typography
                variant="body2"
                sx={{ color: "#DC143C" }}
                role="alert"
              >
                {t("errors.emptyField")}
              </Typography>
            )}
            <Controller
              name="careUnit"
              control={control}
              render={() => (
                <TextField
                  defaultValue={transaction.careUnit}
                  size="small"
                  id="careUnit"
                  label={t("careUnit")}
                  {...register("careUnit")}
                />
              )}
            />
            <Controller
              name="reason"
              control={control}
              render={() => (
                <TextField
                  size="small"
                  id="reason"
                  label={t("reason")}
                  {...register("reason")}
                />
              )}
            />

            <Controller
              name="amount"
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextField
                  defaultValue={transaction.amount}
                  value={value}
                  size="small"
                  error={errors.amount ? true : false}
                  id="amount"
                  label={t("amount") + " *"}
                  {...register("amount", {
                    required: true,
                    validate: isNumber,
                  })}
                />
              )}
            />

            {errors.amount && errors.amount.type === "required" && (
              <Typography
                variant="body2"
                sx={{ color: "#DC143C" }}
                role="alert"
              >
                {t("errors.emptyField")}
              </Typography>
            )}

            {errors.amount && errors.amount.type === "validate" && (
              <Typography
                variant="body2"
                sx={{ color: "#DC143C" }}
                role="alert"
              >
                {t("errors.onlyNumbers")}
              </Typography>
            )}
            <Stack direction={"column"}>
              <LocalizationProvider
                adapterLocale={sv}
                dateAdapter={AdapterDateFns}
              >
                <DatePicker
                  label={t("date")}
                  mask=""
                  value={transactionDate}
                  onChange={handleTransactionDateChange}
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        placeholder: t("dateFormat"),
                      }}
                      error={transactionDateError}
                    />
                  )}
                />
              </LocalizationProvider>
              {transactionDateError ? (
                <Typography
                  variant="body2"
                  sx={{ color: "#DC143C" }}
                  role="alert"
                >
                  {t("errors.invalidDate")}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => dispatch({ type: "MODAL_CANCEL" })}
          >
            {t("cancel")}
          </Button>
          <Button variant="contained" color="success" type="submit">
            {t("edit")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
